/**
 * 💳 Paywall - Premium-Upgrade Modal
 * 
 * Verwendet Google Play Billing via RevenueCat für echte Zahlungen
 */
import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from '../hooks/useTranslation';
import { usePremium } from '../contexts/PremiumContext';
import './Paywall.css';

export default function Paywall({ isOpen, onClose, darkMode, feature }) {
  const { t } = useTranslation();
  const { purchase, restorePurchases, products, isPurchasing } = usePremium();
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await purchase(selectedPlan);
      
      if (result.success) {
        setIsProcessing(false);
        onClose();
        alert(t('premium.purchaseSuccess'));
      } else if (result.cancelled) {
        // User hat abgebrochen - nichts tun
        setIsProcessing(false);
      } else {
        setIsProcessing(false);
        setError(result.error || t('premium.purchaseError'));
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setIsProcessing(false);
      setError(t('premium.purchaseError'));
    }
  };

  const handleRestore = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await restorePurchases();
      
      if (result.restored) {
        setIsProcessing(false);
        onClose();
        alert(t('premium.restoreSuccess') || 'Käufe erfolgreich wiederhergestellt!');
      } else {
        setIsProcessing(false);
        alert(t('premium.noPurchasesFound') || 'Keine Käufe gefunden.');
      }
    } catch (err) {
      console.error('Restore error:', err);
      setIsProcessing(false);
      setError(t('premium.restoreError') || 'Fehler beim Wiederherstellen');
    }
  };

  // Preise aus Products oder Fallback
  const monthlyPrice = products?.monthly?.priceString || '19,99€';
  const yearlyPrice = products?.yearly?.priceString || '199,99€';

  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="paywall-overlay" onClick={onClose}>
      <div 
        className={`paywall-modal ${darkMode ? 'dark' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="paywall-header">
          <span className="paywall-star">⭐</span>
          <h2>{t('premium.title')}</h2>
          <button className="paywall-close" onClick={onClose}>×</button>
        </div>

        {/* Feature Highlight */}
        {feature && (
          <div className="paywall-feature-highlight">
            <span className="paywall-lock">🔒</span>
            <p>{t('premium.featureRequired', { feature: t(`premium.features.${feature}`) })}</p>
          </div>
        )}

        {/* Benefits */}
        <div className="paywall-benefits">
          <h3>{t('premium.benefits')}</h3>
          <div className="paywall-benefit-cards">
            <div className="benefit-card">
              <div className="benefit-text">{t('premium.benefit.cloud')}</div>
            </div>
            <div className="benefit-card">
              <div className="benefit-text">{t('premium.benefit.multiDevice')}</div>
            </div>
            <div className="benefit-card">
              <div className="benefit-text">{t('premium.benefit.tabletMode')}</div>
            </div>
            <div className="benefit-card">
              <div className="benefit-text">{t('premium.benefit.backup')}</div>
            </div>
          </div>
        </div>

        {/* Free Features */}
        <div className="paywall-free">
          <p className="paywall-free-note">
            ✅ {t('premium.freeNote')}
          </p>
        </div>

        {/* Plans */}
        <div className="paywall-plans">
          <button 
            className={`paywall-plan ${selectedPlan === 'monthly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <div className="plan-name">{t('premium.monthly')}</div>
            <div className="plan-price">{monthlyPrice}</div>
            <div className="plan-period">{t('premium.perMonth')}</div>
          </button>
          
          <button 
            className={`paywall-plan ${selectedPlan === 'yearly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('yearly')}
          >
            <div className="plan-badge">{t('premium.save2Months')}</div>
            <div className="plan-name">{t('premium.yearly')}</div>
            <div className="plan-price">{yearlyPrice}</div>
            <div className="plan-period">{t('premium.perYear')}</div>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="paywall-error">
            ⚠️ {error}
          </div>
        )}

        {/* Web-Hinweis */}
        {!isNative && (
          <div className="paywall-web-notice">
            📱 {t('premium.webNotice') || 'In-App Käufe sind nur in der Android-App verfügbar.'}
          </div>
        )}

        {/* CTA */}
        <button 
          className="paywall-cta"
          onClick={handlePurchase}
          disabled={isProcessing || isPurchasing || !isNative}
        >
          {isProcessing || isPurchasing ? t('premium.processing') : t('premium.subscribe')}
        </button>

        {/* Restore Purchases Button */}
        {isNative && (
          <button 
            className="paywall-restore"
            onClick={handleRestore}
            disabled={isProcessing || isPurchasing}
          >
            {t('premium.restorePurchases') || 'Käufe wiederherstellen'}
          </button>
        )}

        {/* Cancel Note */}
        <p className="paywall-cancel-note">
          {t('premium.cancelAnytime')}
        </p>
      </div>
    </div>
  );
}

// Feature Gate Component - zeigt Paywall wenn Feature nicht verfügbar
export function PremiumFeatureGate({ feature, children, darkMode }) {
  const { hasFeature } = usePremium();
  const [showPaywall, setShowPaywall] = useState(false);

  if (hasFeature(feature)) {
    return children;
  }

  return (
    <>
      <div onClick={() => setShowPaywall(true)}>
        {children}
      </div>
      <Paywall 
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        darkMode={darkMode}
        feature={feature}
      />
    </>
  );
}
